import { useEffect, useMemo, useState } from "react"
import { CandidatesList } from "../../components/candidatesList"
import { SearchBar } from "../../components/searchBar"
import { CandidateItemProps, AvailabilityInterval } from "../../components/candidateItem/model"
import { buildWindow, windowFromInterval, windowsOverlap } from "../../utils/availability"
import { getAllCandidates } from "../../services/candidates-service"
import { useCounties, useCitiesByCounty } from "../../hooks/location-hooks"
import {
  CandidatesFilterModal,
  CandidateFilters,
} from "../../components/candidatesFilterModal"
import { MySubscription } from "../../components/mySubscription"

const defaultFilters: CandidateFilters = {
  sort: "none",
  county: "",
  cityId: "",
  availableOn: "",
  availableFrom: "",
  availableTo: "",
  availableTimeFrom: "",
  availableTimeTo: "",
  gigTitle: "",
  categoryId: "",
  category: "",
}

export const Home = () => {
  const [candidates, setCandidates] = useState<Array<CandidateItemProps>>([])
  const [searchString, setSearchString] = useState("")
  const [filters, setFilters] = useState<CandidateFilters>(defaultFilters)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { counties } = useCounties()
  // Doar localitatile judetului filtrat, nu toate cele peste 3000.
  const { cities } = useCitiesByCounty(filters.county)

  useEffect(() => {
    fetchCandidates()
  }, [])

  const fetchCandidates = async () => {
    setIsLoading(true)
    const response = (await getAllCandidates()) as Array<CandidateItemProps>
    setCandidates(response)
    setIsLoading(false)
  }

  const allGigTitles = useMemo<string[]>(() => {
    const seen = new Set<string>()
    candidates.forEach((c) => {
      const titles = (c as any).preferredGigTitles as string[] | undefined
      titles?.forEach((t) => seen.add(t))
    })
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  }, [candidates])

  // `cities` contine deja doar judetul filtrat, deci nu mai e nevoie de filtrare locala.
  const cityIdsForCounty = useMemo(() => {
    if (!filters.county) return null
    return new Set(cities.map((c) => String(c.id)))
  }, [cities, filters.county])

  const cityNamesForCounty = useMemo(() => {
    if (!filters.county) return null
    return cities.map((c) => c.name.toLowerCase())
  }, [cities, filters.county])

  const selectedCityName = useMemo(() => {
    if (!filters.cityId) return null
    return cities.find((c) => String(c.id) === filters.cityId)?.name.toLowerCase() ?? null
  }, [cities, filters.cityId])

  const filteredCandidates = useMemo(() => {
    let result = candidates.filter((c) => {
      const matchesSearch =
        !searchString ||
        c.firstName?.toLowerCase().includes(searchString.toLowerCase()) ||
        c.lastName?.toLowerCase().includes(searchString.toLowerCase())

      const locationLower = c.location?.toLowerCase() ?? ""

      const matchesCounty = !cityIdsForCounty || (
        c.cityId != null
          ? cityIdsForCounty.has(String(c.cityId))
          : cityNamesForCounty!.some((name) => locationLower.startsWith(name))
      )

      const matchesCity = !filters.cityId || (
        c.cityId != null
          ? String(c.cityId) === filters.cityId
          : selectedCityName != null && locationLower.startsWith(selectedCityName)
      )

      const matchesAvailability = (() => {
        const avail = (c as any).availability as AvailabilityInterval[] | undefined

        // Cautarea angajatorului e ea insasi un tipar zilnic — "12-14 aug, intre
        // 18:00 si 22:00" inseamna trei seri — asa ca o normalizam la fel ca pe
        // disponibilitatea candidatului si le intersectam.
        const searched = filters.availableOn
          ? buildWindow(
              filters.availableOn,
              filters.availableOn,
              filters.availableTimeFrom,
              filters.availableTimeTo
            )
          : filters.availableFrom && filters.availableTo
            ? buildWindow(
                filters.availableFrom,
                filters.availableTo,
                filters.availableTimeFrom,
                filters.availableTimeTo
              )
            : null

        if (!searched) return true
        if (!avail || avail.length === 0) return false
        return avail.some((a) => {
          const window = windowFromInterval(a)
          return window !== null && windowsOverlap(searched, window)
        })
      })()

      const matchesGigTitle = !filters.gigTitle || (
        ((c as any).preferredGigTitles as string[] | undefined)?.includes(filters.gigTitle) ?? false
      )

      return matchesSearch && matchesCounty && matchesCity && matchesAvailability && matchesGigTitle
    })

    if (filters.sort === "rating_desc") {
      result = [...result].sort(
        (a, b) => (parseFloat(b.rating) || 0) - (parseFloat(a.rating) || 0)
      )
    } else if (filters.sort === "rating_asc") {
      result = [...result].sort(
        (a, b) => (parseFloat(a.rating) || 0) - (parseFloat(b.rating) || 0)
      )
    }

    return result
  }, [candidates, searchString, filters])

  const activeFilterCount = [
    filters.sort !== "none",
    !!filters.county,
    !!filters.cityId,
    !!filters.availableOn,
    !!(filters.availableFrom && filters.availableTo),
    !!(filters.availableTimeFrom && filters.availableTimeTo),
    !!filters.category,
    !!filters.gigTitle,
  ].filter(Boolean).length

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-4 sm:px-6 sm:py-6 flex-shrink-0">
        <SearchBar
          text="Caută candidat"
          onChange={(e) => setSearchString((e.target as HTMLInputElement).value)}
        />

        <div className="mt-5 sm:mt-6 flex items-center justify-between">
          <div>
            <h3 className="page-title text-[24px] sm:text-[28px] md:text-[32px]">
              Bine ai venit!
            </h3>
            <p className="page-description text-sm sm:text-base">
              Lista cu cei mai recenți candidați.
            </p>
          </div>

          <button
            onClick={() => setIsFilterOpen(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-colors flex-shrink-0 ${
              activeFilterCount > 0
                ? "bg-figmaPrimary border-figmaPrimary text-white"
                : "bg-white border-[#E5E7EB] text-[#180E73]"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 4h12M4 8h8M6 12h4"
                stroke={activeFilterCount > 0 ? "#fff" : "#180E73"}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Filtrează{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
        {/* Abonamentul meu (plan + cote rămase) — se ascunde singur fără abonament activ */}
        <MySubscription />

        <CandidatesList
          url="candidates"
          candidates={filteredCandidates}
          loading={isLoading}
          emptyMessage="Nu am găsit candidați care să corespundă criteriilor."
        />
      </div>

      <CandidatesFilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        counties={counties}
        gigTitles={allGigTitles}
        filters={filters}
        onApply={setFilters}
      />
    </div>
  )
}
